<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type { StartTransactionRequest } from '@/types/carts-transactions-api';

import MobileModal from '@/components/MobileModal.vue';
import { useTransactionSSE } from '@/composables/carts/useTransactionSSE';

const props = defineProps<{
  cartsTransactionsApi: CartsTransactionsApi;
}>();

const emit = defineEmits<{
  paymentCompleted: [];
  paymentFailed: [];
  partialFailure: [data: { failedBookingIds: string[]; failureReasons: Record<string, string> }];
  transactionStarted: [];
}>();

const { t, locale } = useI18n();

const { paymentStatus, failedBookingIds, failureReasons, connect: connectSSE, disconnect: disconnectSSE } = useTransactionSSE(
  () => props.cartsTransactionsApi.getTransactionEventsUrl(),
);

const isModalOpen = ref(false);
const isLoading = ref(false);
const error = ref<string | null>(null);
const widgetUrl = ref<string | null>(null);
const iFrameProviderWidgetHeight = ref<number>(0);
const iframeLoadCount = ref(0);
const isAwaitingPaymentConfirmation = ref(false);

const isProcessing = computed(() =>
  isAwaitingPaymentConfirmation.value || paymentStatus.value === 'confirmed' || paymentStatus.value === 'captured',
);

// Once the payment is being confirmed/captured, closing would interrupt the booking
// creation and the final SSE event that triggers the success screen — block it there,
// but allow closing while the Stripe iframe itself is shown (transaction resumability
// makes it safe: reopening the cart offers a "resume payment" flow instead of starting
// a duplicate transaction).
const isClosable = computed(() => !isProcessing.value);

const processingMessage = computed(() => {
  if (paymentStatus.value === 'captured')
    return t('cart.payment.steps.finalized');
  if (paymentStatus.value === 'confirmed')
    return t('cart.payment.steps.bookingInProgress');
  if (isAwaitingPaymentConfirmation.value)
    return t('cart.payment.steps.paymentVerification');
  return '';
});

const iframeMinHeight = computed(() => {
  if (iFrameProviderWidgetHeight.value === 0)
    return '400px';
  return `${iFrameProviderWidgetHeight.value}px`;
});

function handleIframeMessage(event: MessageEvent) {
  if (event.data?.type === 'widget-resize') {
    iFrameProviderWidgetHeight.value = event.data.widgetHeight;
  }
}

function handleIframeLoad() {
  iframeLoadCount.value++;
  if (iframeLoadCount.value >= 2) {
    isAwaitingPaymentConfirmation.value = true;
  }
}

function openModal() {
  isModalOpen.value = true;
  error.value = null;
  widgetUrl.value = null;
  iframeLoadCount.value = 0;
  isAwaitingPaymentConfirmation.value = false;
  startPaymentWorkflow();
}

function closeModal() {
  if (paymentStatus.value === 'failed') {
    emit('paymentFailed');
  }
  disconnectSSE();
  isModalOpen.value = false;
  widgetUrl.value = null;
}

watch(paymentStatus, (status) => {
  if (status === 'captured') {
    setTimeout(() => {
      emit('paymentCompleted');
      closeModal();
    }, 3000);
  }

  if (status === 'partial_failure') {
    emit('partialFailure', {
      failedBookingIds: failedBookingIds.value,
      failureReasons: failureReasons.value,
    });
    closeModal();
  }
});

async function startPaymentWorkflow() {
  isLoading.value = true;
  error.value = null;

  try {
    const request: StartTransactionRequest = {
      locale: locale.value || 'fr-FR',
      returnUrl: window.location.href,
    };

    const response = await props.cartsTransactionsApi.startTransaction(request);
    emit('transactionStarted');

    if (response.widget !== null) {
      widgetUrl.value = props.cartsTransactionsApi.getWidgetUrl();
    }
    else {
      isAwaitingPaymentConfirmation.value = true;
    }
    connectSSE();
  }
  catch (err: unknown) {
    console.error('Error starting transaction:', err);
    error.value = t('cart.payment.error');
  }
  finally {
    isLoading.value = false;
  }
}

async function resumeExistingTransaction() {
  isModalOpen.value = true;
  isLoading.value = true;
  error.value = null;
  widgetUrl.value = null;
  iframeLoadCount.value = 0;
  isAwaitingPaymentConfirmation.value = false;

  try {
    const resumeResult = await props.cartsTransactionsApi.resumeTransaction();

    if (resumeResult.status === 'COMPLETED') {
      emit('paymentCompleted');
      closeModal();
      return;
    }

    if (resumeResult.status === 'FAILED') {
      error.value = t('cart.payment.resumeError');
      return;
    }

    widgetUrl.value = props.cartsTransactionsApi.getWidgetUrl();
    connectSSE();
  }
  catch (err: unknown) {
    console.error('[PaymentModal] Error resuming transaction:', err);
    error.value = t('cart.payment.resumeError');
  }
  finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleIframeMessage);
  disconnectSSE();
});

defineExpose({ openModal, closeModal, resumeExistingTransaction });
</script>

<template>
  <MobileModal
    :is-open="isModalOpen"
    :title="t('cart.payment.title')"
    :closable="isClosable"
    @close="closeModal"
  >
    <template #body>
      <div class="payment-modal-body">
        <div v-if="paymentStatus === 'failed'" class="error-state" role="alert">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            />
          </svg>
          <p>{{ t('cart.payment.failed') }}</p>
          <button class="retry-button" @click="startPaymentWorkflow">
            {{ t('cart.payment.retry') }}
          </button>
        </div>

        <div v-else-if="isProcessing" class="processing-state" role="status">
          <svg
            v-if="paymentStatus !== 'captured'"
            class="yb-spinner" width="40" height="40" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.3" />
            <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <svg
            v-else
            width="48" height="48" viewBox="0 0 24 24" fill="none"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true"
          >
            <path
              d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            />
            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <p>{{ processingMessage }}</p>
        </div>

        <div v-else-if="isLoading" class="loading-state">
          <svg class="yb-spinner" width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.3" />
            <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          <p>{{ t('cart.payment.loading') }}</p>
        </div>

        <div v-else-if="error" class="error-state" role="alert">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
            />
          </svg>
          <p>{{ error }}</p>
          <button class="retry-button" @click="startPaymentWorkflow">
            {{ t('cart.payment.retry') }}
          </button>
        </div>

        <iframe
          v-else-if="widgetUrl" :src="widgetUrl" :style="{ minHeight: iframeMinHeight }" title="Widget de paiement"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms allow-top-navigation allow-modals allow-payment-request"
          allow="payment *"
          @load="handleIframeLoad"
        />
      </div>
    </template>
  </MobileModal>
</template>

<style scoped lang="css">
.payment-modal-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 200px;
  width: 100%;
}

.loading-state,
.processing-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: var(--yb-spacing-base);
  text-align: center;
  color: var(--yb-color-darkgray);
}

.error-state {
  color: #ea3532;
}

.processing-state svg:not(.yb-spinner) {
  color: #16a34a;
}

.yb-spinner {
  color: var(--yb-color-primary);
}

.retry-button {
  margin-top: var(--yb-spacing-sm);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-weight: var(--yb-text-bold);
  cursor: pointer;
}

iframe {
  width: 100%;
  flex: 1;
  border: none;
}

:deep(.modal-content) {
  min-height: 75vh;
}

:deep(.modal-body) {
  overflow-y: hidden;
  overscroll-behavior: contain;
}
</style>
